/**
 * AxelIntelHub — AXEL INTEL HUB
 *
 * Tab 1: Daily Brief — AI-generated live market bias with Signal Sources breakdown
 * Tab 2: Research Desk — Fixed AxelResearchDesk component
 *
 * IntelHubContext wires the "Full Analysis →" CTA between tabs.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { IntelHubProvider, useIntelHub } from "../context/IntelHubContext";
import AxelResearchDesk from "./AxelResearchDesk";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: "#07090f",
  surf: "#0d1117",
  border: "#1c2030",
  gold: "#F5A623",
  green: "#00d68f",
  red: "#ff4757",
  blue: "#38bdf8",
  purple: "#c084fc",
  text: "#c8d4e0",
  muted: "#4a5568",
  white: "#eef2f7",
};

// ── Shared auth fetch helper ──────────────────────────────────────────────────
async function authFetch(url, options = {}) {
  const token = localStorage.getItem("flexx_access_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ── Macro Pulse tile ──────────────────────────────────────────────────────────
function MacroTile({ label, data }) {
  if (!data) return null;
  const dir = data.direction?.toUpperCase() || "NEUTRAL";
  const col = dir === "BULLISH" ? T.green : dir === "BEARISH" ? T.red : T.muted;
  const arrow = dir === "BULLISH" ? "▲" : dir === "BEARISH" ? "▼" : "◆";
  return (
    <div style={{
      background: T.surf, border: `1px solid ${T.border}`, borderRadius: 8,
      padding: "10px 12px", minWidth: 110, flex: "1 1 110px",
    }}>
      <div style={{ fontSize: ".58rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 4, fontFamily: "Barlow Condensed, sans-serif" }}>{label}</div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: ".95rem", fontWeight: 600, color: T.white, marginBottom: 3 }}>{data.price}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ color: col, fontSize: ".75rem", fontWeight: 700 }}>{arrow}</span>
        <span style={{ fontSize: ".65rem", color: col, fontFamily: "Barlow Condensed, sans-serif", letterSpacing: ".06em" }}>{dir}</span>
      </div>
      {data.note && <div style={{ fontSize: ".6rem", color: T.muted, marginTop: 4, lineHeight: 1.5, fontFamily: "Barlow, sans-serif" }}>{data.note}</div>}
    </div>
  );
}

// ── Signal Sources grid ───────────────────────────────────────────────────────
function SignalSources({ sources, status }) {
  if (!sources && !status) return null;
  const order = [
    { key: "market_data",     label: "Market Data",  desc: "DXY, XAU, BTC, SPX" },
    { key: "axel_web_search", label: "Web Search",   desc: "Live AXEL search" },
    { key: "newsapi",         label: "NewsAPI",      desc: "Market headlines" },
    { key: "polymarket",      label: "Polymarket",   desc: "Prediction markets" },
    { key: "grok",            label: "Grok / X",     desc: "X sentiment (opt.)" },
  ];
  // status ∈ live | cached | error | disabled | not_configured
  const STYLE = {
    live:           { dot: T.green, text: T.green, bg: "#001a0f", border: T.green + "60", tag: "LIVE" },
    cached:         { dot: T.gold,  text: T.gold,  bg: "#1a1400", border: T.gold + "55",  tag: "CACHED" },
    error:          { dot: T.red,   text: T.red,   bg: "#1a0808", border: T.red + "55",   tag: "ERROR" },
    disabled:       { dot: T.muted, text: T.muted, bg: T.surf,    border: T.border,       tag: "DISABLED" },
    not_configured: { dot: T.muted, text: T.muted, bg: T.surf,    border: T.border,       tag: "NOT CONFIGURED" },
  };
  const resolve = (key) => {
    if (status && status[key]) return status[key].status || "error";
    // Back-compat: old boolean shape → live/disabled
    return sources && sources[key] ? "live" : "disabled";
  };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8, fontFamily: "Barlow Condensed, sans-serif" }}>Signal Sources</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {order.map(({ key, label, desc }) => {
          const st = resolve(key);
          const sty = STYLE[st] || STYLE.error;
          const detail = (status && status[key] && status[key].detail) || desc;
          return (
            <div key={key} data-testid={`signal-source-${key}`} title={`${label} — ${sty.tag}: ${detail}`} style={{
              padding: "5px 10px", borderRadius: 5, background: sty.bg,
              border: `1px solid ${sty.border}`, display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: sty.dot, display: "inline-block", flexShrink: 0 }} />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: ".65rem", color: sty.text, fontFamily: "Barlow Condensed, sans-serif", fontWeight: 700, letterSpacing: ".06em" }}>{label}</span>
                  <span data-testid={`signal-source-${key}-status`} style={{ fontSize: ".5rem", color: sty.text, border: `1px solid ${sty.border}`, borderRadius: 3, padding: "0 4px", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: ".05em" }}>{sty.tag}</span>
                </div>
                <div style={{ fontSize: ".57rem", color: T.muted, fontFamily: "Barlow, sans-serif" }}>{detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Live Headlines (NewsAPI, normalized) ──────────────────────────────────────
const SENTIMENT_STYLE = {
  bullish: { c: "#00d68f", bg: "#001a0f", label: "BULLISH", arrow: "▲" },
  bearish: { c: "#ff4757", bg: "#1a0808", label: "BEARISH", arrow: "▼" },
  neutral: { c: "#8a94a6", bg: "#12151c", label: "NEUTRAL", arrow: "•" },
  unknown: { c: "#8a94a6", bg: "#12151c", label: "MIXED", arrow: "•" },
};

function timeAgoShort(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function NewsHeadlines({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 18 }} data-testid="news-headlines">
      <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8, fontFamily: "Barlow Condensed, sans-serif" }}>
        Market Headlines <span style={{ color: T.gold }}>· NewsAPI</span>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((h, i) => {
          const s = SENTIMENT_STYLE[h.sentimentHint] || SENTIMENT_STYLE.neutral;
          return (
            <a key={h.url || i} href={h.url || "#"} target="_blank" rel="noopener noreferrer"
              data-testid={`news-headline-${i}`}
              style={{ display: "block", textDecoration: "none", background: T.surf, border: `1px solid ${T.border}`, borderLeft: `3px solid ${s.c}`, borderRadius: 8, padding: "10px 12px", transition: "border-color .18s, background-color .18s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = s.c; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = s.c; }}>
              <div style={{ fontSize: ".8rem", color: T.white, fontFamily: "Barlow, sans-serif", lineHeight: 1.45, marginBottom: 6 }}>{h.title}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span data-testid={`news-headline-${i}-sentiment`} style={{ fontSize: ".54rem", fontWeight: 800, letterSpacing: ".06em", color: s.c, background: s.bg, border: `1px solid ${s.c}55`, borderRadius: 4, padding: "1px 6px", fontFamily: "'IBM Plex Mono', monospace" }}>{s.arrow} {s.label}</span>
                {(h.assetTags || []).slice(0, 3).map((tag) => (
                  <span key={tag} style={{ fontSize: ".54rem", color: T.gold, border: `1px solid ${T.gold}44`, borderRadius: 4, padding: "1px 6px", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: ".04em" }}>{tag}</span>
                ))}
                <span style={{ fontSize: ".57rem", color: T.muted, fontFamily: "Barlow, sans-serif", marginLeft: "auto" }}>
                  {h.source}{h.publishedAt ? ` · ${timeAgoShort(h.publishedAt)}` : ""}
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}


// ── Session Bias badge ────────────────────────────────────────────────────────
function SessionBiasRow({ sessionBias }) {
  if (!sessionBias) return null;
  const sessions = [
    { key: "london", label: "London" },
    { key: "ny",     label: "New York" },
    { key: "asia",   label: "Asia" },
  ];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
      {sessions.map(({ key, label }) => {
        const val = (sessionBias[key] || "NEUTRAL").toUpperCase();
        const col = val === "BULLISH" ? T.green : val === "BEARISH" ? T.red : T.muted;
        return (
          <div key={key} style={{
            padding: "5px 12px", borderRadius: 5,
            background: col + "15", border: `1px solid ${col}40`,
            fontFamily: "Barlow Condensed, sans-serif", fontSize: ".78rem",
            fontWeight: 700, letterSpacing: ".08em", color: col,
          }}>
            {label}: {val}
          </div>
        );
      })}
    </div>
  );
}

// ── Levels row ────────────────────────────────────────────────────────────────
function LevelsRow({ label, levels, color }) {
  if (!levels) return null;
  const keys = ["strong_resistance", "resistance", "pivot", "support", "strong_support"];
  const labels = ["S.Res", "Res", "Pivot", "Sup", "S.Sup"];
  const cols = [T.red, "#ff8f8f", T.muted, "#8fd68f", T.green];
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 5, fontFamily: "Barlow Condensed, sans-serif" }}>{label} Key Levels</div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {keys.map((k, i) => levels[k] && (
          <div key={k} style={{ padding: "4px 9px", borderRadius: 4, background: T.surf, border: `1px solid ${cols[i]}40`, textAlign: "center" }}>
            <div style={{ fontSize: ".57rem", color: T.muted, fontFamily: "Barlow Condensed, sans-serif", letterSpacing: ".08em" }}>{labels[i]}</div>
            <div style={{ fontSize: ".75rem", fontFamily: "'IBM Plex Mono', monospace", color: cols[i], fontWeight: 600 }}>{levels[k]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Key Events list ───────────────────────────────────────────────────────────
function KeyEvents({ events }) {
  if (!events?.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8, fontFamily: "Barlow Condensed, sans-serif" }}>Economic Events Today</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {events.map((ev, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 10px", background: T.surf, border: `1px solid ${T.border}`, borderRadius: 6 }}>
            <span style={{ color: T.gold, fontSize: ".65rem", fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>📅</span>
            <span style={{ fontSize: ".73rem", color: T.text, fontFamily: "Barlow, sans-serif", lineHeight: 1.5 }}>{ev}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Daily Brief tab ───────────────────────────────────────────────────────────
function _relativeTime(iso) {
  if (!iso) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "1 min ago";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 5400) return "1 hr ago";
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} hr ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
}

function DailyBriefTab() {
  const { setActiveTab, setFullAnalysisPayload, lastBriefData, setLastBriefData } = useIntelHub();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [, setTick] = useState(0);

  // tick every 30s so the "X min ago" label stays fresh without re-fetching
  useEffect(() => {
    if (!generatedAt) return;
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [generatedAt]);

  const fetchBrief = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(
        `${API_URL}/api/axel/daily-brief${forceRefresh ? "?force_refresh=true" : ""}`,
        { method: "POST" }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const detail = d?.detail;
        if (typeof detail === "object" && detail?.wait_seconds) {
          throw new Error(`Rate limit. Try again in ${detail.wait_seconds}s.`);
        }
        throw new Error(typeof detail === "string" ? detail : `Error ${res.status}`);
      }
      const data = await res.json();
      setLastBriefData(data);
      setGeneratedAt(data.generated_at);
    } catch (e) {
      setError(e.message || "Failed to fetch AXEL Brief. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [setLastBriefData]);

  useEffect(() => {
    if (!lastBriefData) fetchBrief();
  }, [lastBriefData, fetchBrief]);

  function handleFullAnalysis(asset = "XAUUSD") {
    if (!lastBriefData) return;
    const brief = lastBriefData.morning_brief || "";
    const verdict = lastBriefData.axel_verdict || "";
    const systemPrompt = `You are AXEL, Flexx Academy AI Research Intelligence. Use web search NOW.
Today's AXEL Brief context: ${brief.slice(0, 400)}`;
    const userMessage = `Run a deep AXEL BIAS analysis on ${asset} using today's macro context. Search for current live price and recent news.`;
    setFullAnalysisPayload({ asset, systemPrompt, userMessage });
    setActiveTab("research");
  }

  const brief = lastBriefData;
  const overallBias = brief?.session_bias?.overall?.toUpperCase() || "MIXED";
  const biasCol = overallBias === "BULLISH" ? T.green : overallBias === "BEARISH" ? T.red : T.muted;

  return (
    <div data-testid="daily-brief-tab" style={{ maxWidth: 820, margin: "0 auto", padding: "0 14px 80px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.3rem", fontWeight: 800, color: T.white, letterSpacing: ".04em" }}>
            AXEL <span style={{ color: T.gold }}>Daily Brief</span>
          </div>
          {generatedAt && (
            <div
              data-testid="daily-brief-generated-at"
              title={`Generated ${new Date(generatedAt).toLocaleString()}`}
              style={{ fontSize: ".6rem", color: T.muted, fontFamily: "Barlow, sans-serif", marginTop: 2 }}
            >
              Generated {_relativeTime(generatedAt)}
              {brief?.cached && <span style={{ color: T.gold, marginLeft: 6 }}>· cached</span>}
            </div>
          )}
        </div>
        <button
          onClick={() => fetchBrief(true)}
          disabled={loading}
          data-testid="refresh-brief-btn"
          style={{
            padding: "8px 14px", background: "transparent", border: `1px solid ${T.border}`,
            borderRadius: 6, color: loading ? T.muted : T.gold, cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "Barlow Condensed, sans-serif", fontSize: ".8rem", fontWeight: 700, letterSpacing: ".08em",
          }}
        >
          {loading ? "⏳ LOADING..." : "↺ REFRESH"}
        </button>
      </div>

      {error && (
        <div style={{ padding: "12px 14px", background: "#1a0810", border: `1px solid #5a1030`, borderRadius: 8, marginBottom: 16, color: "#ff6b88", fontSize: ".8rem", fontFamily: "Barlow, sans-serif" }}>
          {error}
        </div>
      )}

      {loading && !brief && (
        <div style={{ padding: "60px 20px", textAlign: "center" }}>
          <div style={{ width: 18, height: 18, border: "2px solid #2a3040", borderTopColor: T.gold, borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block", marginBottom: 16 }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: ".95rem", color: T.white, letterSpacing: ".08em", marginBottom: 8 }}>GENERATING AXEL BRIEF...</div>
          <div style={{ fontSize: ".72rem", color: T.muted, fontFamily: "Barlow, sans-serif" }}>Searching live market data, news, and macro signals...</div>
        </div>
      )}

      {brief && <>
        {/* Overall bias banner */}
        <div style={{
          padding: "14px 16px", borderRadius: 9, marginBottom: 16,
          background: biasCol + "10", border: `1px solid ${biasCol}40`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: ".65rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 4 }}>Overall Session Bias</div>
            <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.4rem", fontWeight: 800, color: biasCol, letterSpacing: ".04em" }}>
              {overallBias === "BULLISH" ? "▲ " : overallBias === "BEARISH" ? "▼ " : "◆ "}{overallBias}
            </div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, animation: "pdot 1.4s ease-in-out infinite", display: "inline-block" }} />
            <style>{`@keyframes pdot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.3;transform:scale(0.6)}}`}</style>
            <span style={{ fontSize: ".65rem", color: T.green, fontFamily: "Barlow Condensed, sans-serif", letterSpacing: ".1em" }}>LIVE</span>
          </div>
        </div>

        {/* Session bias */}
        <SessionBiasRow sessionBias={brief.session_bias} />

        {/* Signal Sources */}
        <SignalSources sources={brief.signal_sources} status={brief.source_status} />

        {/* Live Headlines (NewsAPI) */}
        <NewsHeadlines items={brief.news_headlines} />

        {/* Macro Pulse */}
        {brief.macro_pulse && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8, fontFamily: "Barlow Condensed, sans-serif" }}>Macro Pulse</div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              <MacroTile label="DXY" data={brief.macro_pulse.dxy} />
              <MacroTile label="XAU/USD" data={brief.macro_pulse.xauusd} />
              <MacroTile label="BTC/USD" data={brief.macro_pulse.btcusd} />
              <MacroTile label="S&P 500" data={brief.macro_pulse.sp500} />
              <MacroTile label="VIX" data={brief.macro_pulse.vix} />
              <MacroTile label="US 10Y" data={brief.macro_pulse.us10y} />
            </div>
          </div>
        )}

        {/* Key levels */}
        {brief.xau_levels && <LevelsRow label="XAUUSD" levels={brief.xau_levels} color={T.gold} />}
        {brief.btc_levels && <LevelsRow label="BTCUSD" levels={brief.btc_levels} color="#F97316" />}

        {/* Morning Brief text */}
        {brief.morning_brief && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 8, fontFamily: "Barlow Condensed, sans-serif" }}>AXEL Morning Brief</div>
            <div style={{
              background: T.surf, border: `1px solid ${T.border}`, borderRadius: 9,
              padding: "16px 15px", fontSize: ".82rem", color: T.text,
              fontFamily: "Barlow, sans-serif", lineHeight: 1.8,
              whiteSpace: "pre-wrap",
            }}>
              {brief.morning_brief}
            </div>
          </div>
        )}

        {/* AXEL Verdict */}
        {brief.axel_verdict && (
          <div style={{
            padding: "14px 16px", background: T.gold + "0d", border: `1px solid ${T.gold}40`,
            borderLeft: `3px solid ${T.gold}`, borderRadius: 8, marginBottom: 16,
          }}>
            <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 6, fontFamily: "Barlow Condensed, sans-serif" }}>AXEL Verdict</div>
            <div style={{ fontSize: ".85rem", color: T.white, fontFamily: "Barlow, sans-serif", lineHeight: 1.7 }}>{brief.axel_verdict}</div>
          </div>
        )}

        {/* Risk Note */}
        {brief.risk_note && (
          <div style={{
            padding: "12px 14px", background: "#1a0a00", border: `1px solid ${T.red}30`,
            borderLeft: `3px solid ${T.red}`, borderRadius: 8, marginBottom: 16,
          }}>
            <div style={{ fontSize: ".6rem", color: T.red, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 4, fontFamily: "Barlow Condensed, sans-serif" }}>Risk to Watch</div>
            <div style={{ fontSize: ".8rem", color: T.text, fontFamily: "Barlow, sans-serif", lineHeight: 1.6 }}>{brief.risk_note}</div>
          </div>
        )}

        {/* Forex Scan */}
        {brief.forex_scan && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 6, fontFamily: "Barlow Condensed, sans-serif" }}>Tier 3 FIAT / FOREX Quick Scan</div>
            <div style={{
              background: T.surf, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: "12px 14px", fontSize: ".8rem", color: T.text,
              fontFamily: "Barlow, sans-serif", lineHeight: 1.7,
            }}>
              {brief.forex_scan}
            </div>
          </div>
        )}

        {/* Key Events */}
        <KeyEvents events={brief.key_events} />

        {/* Full Analysis CTA */}
        <div style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 10, padding: "16px 15px", marginBottom: 16 }}>
          <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: ".95rem", fontWeight: 800, color: T.white, letterSpacing: ".04em", marginBottom: 6 }}>Full Analysis →</div>
          <div style={{ fontSize: ".73rem", color: T.muted, fontFamily: "Barlow, sans-serif", lineHeight: 1.6, marginBottom: 14 }}>
            Use today's macro context to run a deep AXEL analysis on any asset in the Research Desk.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["XAUUSD", "BTCUSD", "USOIL", "UKOIL"].map(a => (
              <button key={a} onClick={() => handleFullAnalysis(a)} data-testid={`full-analysis-${a}`}
                style={{
                  flex: 1, minWidth: 90, padding: "10px 8px",
                  background: "transparent", border: `1.5px solid ${T.gold}`,
                  borderRadius: 7, color: T.gold, fontFamily: "Barlow Condensed, sans-serif",
                  fontSize: ".85rem", fontWeight: 700, letterSpacing: ".08em", cursor: "pointer",
                  transition: "all .15s",
                }}
              >
                {a === "XAUUSD" ? "🥇" : a === "BTCUSD" ? "₿" : "🛢"} {a}
              </button>
            ))}
            <button onClick={() => setActiveTab("research")} data-testid="open-research-desk-btn"
              style={{
                flex: 1, minWidth: 100, padding: "10px 8px",
                background: "transparent", border: `1.5px solid ${T.border}`,
                borderRadius: 7, color: T.text, fontFamily: "Barlow Condensed, sans-serif",
                fontSize: ".82rem", fontWeight: 700, letterSpacing: ".06em", cursor: "pointer",
              }}
            >
              🔍 Open Desk
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ padding: "12px 14px", background: "#050710", border: `1px solid #1a2030`, borderRadius: 8, textAlign: "center", fontSize: ".6rem", color: "#2a3545", letterSpacing: ".1em", textTransform: "uppercase", lineHeight: 2, fontFamily: "Barlow Condensed, sans-serif" }}>
          For educational purposes only. Not financial advice. Always manage your own risk.<br />
          AXEL Intel · Flexx Academy · Powered by Claude AI + Live Web Search
        </div>
      </>}
    </div>
  );
}

// ── Research Desk tab (wraps the fixed component) ─────────────────────────────
function ResearchDeskTab({ pmirAccess }) {
  const { fullAnalysisPayload, setFullAnalysisPayload } = useIntelHub();
  // If a Full Analysis payload was passed from Daily Brief, it's handled inside AxelResearchDesk
  return (
    <div data-testid="research-desk-tab">
      <AxelResearchDesk
        pmirAccess={pmirAccess}
        fullAnalysisPayload={fullAnalysisPayload}
        onFullAnalysisClear={() => setFullAnalysisPayload(null)}
      />
    </div>
  );
}

// ── Tab switcher component ────────────────────────────────────────────────────
import QuantLabTab from "./QuantLabTab";
import SignalJournalTab from "./SignalJournalTab";
import PerformanceCardTab from "./PerformanceCardTab";
import AXELAssetChart from "./AXELAssetChart";

function IntelHubInner() {
  const { user } = useAuth();
  const { activeTab, setActiveTab } = useIntelHub();
  const pmirAccess = !!user?.pmir_access;
  const quantBetaEnabled = !!user?.axel_quant_beta_enabled;
  const isAdmin = user?.role === "admin";

  const tabs = [
    { id: "brief",    label: "Daily Brief",    icon: "⚡", testId: "intel-tab-brief" },
    { id: "research", label: "Research Desk",  icon: "🔍", testId: "intel-tab-research" },
    { id: "chart",    label: "AssetChart",     icon: "📊", testId: "intel-tab-chart" },
    { id: "journal",  label: "Journal",        icon: "📓", testId: "intel-tab-journal" },
    ...(isAdmin
      ? [{ id: "performance", label: "Performance", icon: "📈", testId: "intel-tab-performance" }]
      : []
    ),
    ...(quantBetaEnabled
      ? [{ id: "quant", label: "Quant Lab", icon: "🧪", testId: "intel-tab-quant", beta: true }]
      : []
    ),
  ];

  return (
    <div style={{ minHeight: "100vh", background: T.bg, paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${T.border}`, background: T.surf,
        padding: "12px 18px", position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: "linear-gradient(135deg,#F5A623,#c97d10)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "Barlow Condensed, sans-serif", fontWeight: 800, fontSize: "1rem",
              color: "#07090f", boxShadow: "0 0 14px rgba(245,166,35,0.3)",
            }}>A</div>
            <div>
              <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1rem", fontWeight: 800, color: T.white, letterSpacing: ".03em", lineHeight: 1 }}>
                AXEL <span style={{ color: T.gold }}>INTEL HUB</span>
              </div>
              <div style={{ fontSize: ".57rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", marginTop: 2, fontFamily: "Barlow, sans-serif" }}>
                Flexx Academy · Live AI Market Intelligence
              </div>
            </div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.green, animation: "pdot2 1.4s ease-in-out infinite", display: "inline-block" }} />
            <style>{`@keyframes pdot2{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
            <span style={{ fontSize: ".6rem", color: T.green, letterSpacing: ".1em", fontFamily: "Barlow Condensed, sans-serif" }}>LIVE</span>
          </div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ background: T.surf, borderBottom: `1px solid ${T.border}`, padding: "0 18px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", gap: 2 }}>
          {tabs.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={tab.testId}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "12px 16px", background: "transparent",
                  border: "none", borderBottom: `2px solid ${active ? T.gold : "transparent"}`,
                  color: active ? T.gold : T.muted, cursor: "pointer",
                  fontFamily: "Barlow Condensed, sans-serif", fontSize: ".88rem",
                  fontWeight: 700, letterSpacing: ".06em",
                  display: "flex", alignItems: "center", gap: 6,
                  transition: "all .15s",
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ paddingTop: 16 }}>
        {activeTab === "brief"    && <DailyBriefTab />}
        {activeTab === "research" && <ResearchDeskTab pmirAccess={pmirAccess} />}
        {activeTab === "chart"    && <AXELAssetChart />}
        {activeTab === "journal"  && <SignalJournalTab isAdmin={isAdmin} />}
        {activeTab === "performance" && isAdmin && <PerformanceCardTab />}
        {activeTab === "quant" && quantBetaEnabled && <QuantLabTab />}
      </div>
    </div>
  );
}

// ── Exported page ─────────────────────────────────────────────────────────────
export default function AxelIntelHub() {
  return (
    <IntelHubProvider>
      <IntelHubInner />
    </IntelHubProvider>
  );
}
